/**
 * ReservationSync entity (LOCKER-374 · audit trail)
 * =================================================
 *
 * One record per **locker per run** of the locally-operated reservation-drift
 * tooling. It is the durable answer to two questions nobody could answer before:
 * *how far had this locker drifted when we last looked*, and *what did we change*.
 *
 * ## Why a record per locker, not per run
 *
 * A sweep covers the whole estate, but the unit an operator reasons about is a
 * locker: "is THIS locker in sync, and what did we do to it last time". Keying by
 * run would make a locker's history a scan across runs. The record hangs off the
 * locker's own `gsi2` partition, so a locker's history is one query.
 *
 * ## Why the status is a ladder and not a boolean
 *
 * A sweep with no `--apply` is still a fact worth keeping — it is the assessment
 * that says drift was looked at and left alone. So the record is born at
 * `DRIFT_ASSESSED`, and applying a sync ADVANCES it rather than creating a second
 * record for the same run. `SYNC_REVERTED` is a third rung, not a deletion: the
 * sync report stays readable after the revert, which is the point of an audit
 * trail.
 *
 * ## Why the partner API version is a first-class field
 *
 * v1 and v2 lockers are read through completely different endpoints — v1 has a
 * per-locker reservations endpoint, v2 has none and must be bucketed out of one
 * global pass by `door_id`. A reader who does not know which version produced a
 * report cannot judge its completeness (only v1 reports a per-locker total), so
 * the version is stored, indexed and shown, never inferred at read time.
 */
export const ReservationSyncThingTypeMrn = "mrn:sl2:::reservationSync";

export const ReservationSyncStatusList = [
  /** A sweep ran and the drift report was stored. Nothing was written. */
  "DRIFT_ASSESSED",
  /** A sync wrote at least one row. The drift report is still there. */
  "SYNC_APPLIED",
  /** A revert undid (or attempted to undo) the sync's writes. */
  "SYNC_REVERTED",
  /** The read or the write failed hard enough that the run proves nothing. */
  "SYNC_FAILED",
] as const;

export const ReservationSyncStatusEnum = {
  DRIFT_ASSESSED: "DRIFT_ASSESSED",
  SYNC_APPLIED: "SYNC_APPLIED",
  SYNC_REVERTED: "SYNC_REVERTED",
  SYNC_FAILED: "SYNC_FAILED",
} as const;

export type ReservationSyncStatus = (typeof ReservationSyncStatusList)[number];

/**
 * The four object families a run can produce. `snapshots` is deliberately its own
 * kind rather than part of `drift`: the snapshots are the bulk of the bytes and
 * the UI must be able to render a whole report without fetching any of them.
 */
export const ReservationSyncReportKindList = [
  "drift",
  "sync",
  "revert",
  "snapshots",
] as const;

export type ReservationSyncReportKind =
  (typeof ReservationSyncReportKindList)[number];

/**
 * Default split threshold for a report object, in bytes.
 *
 * 6 MB is a working number, not a limit anyone is forced to live with — it is the
 * DEFAULT behind a script flag (`--max-object-bytes`) precisely because one
 * pathological locker should not need a code change. Reports are stored
 * uncompressed: at this volume gzip saves pennies and costs you being able to
 * `aws s3 cp … | jq` an audit object without unpacking it.
 *
 * Raised from 4 MB once a 25k-reservation locker put ~64 MB of snapshots in front
 * of the console. Nothing on the serving side caps this: the object is handed over
 * as a presigned URL the browser fetches directly, so the Lambda's own 6 MB
 * response limit never applies. The real cost of a larger part is the single-row
 * case — opening one row downloads the whole part — and that is bounded by the
 * console caching a fetched part instead of re-fetching it per row.
 */
export const DEFAULT_MAX_OBJECT_BYTES = 6 * 1024 * 1024;

/** The SSM parameter publishing the report bucket's name, written by the SAM template. */
export const RESERVATION_SYNC_BUCKET_SSM_PARAM = "/sl2/sst-sync-bucket-name";

/**
 * The one stored object whose SST-id range covers `sstId`, or `undefined`.
 *
 * Lives in `shared/` rather than next to the chunker because BOTH sides need the
 * same answer: the scripts write the ranges, and the console resolves a row to a
 * single object with them. Two implementations of this lookup would eventually
 * disagree, and the symptom would be a console that fetches the wrong part and
 * reports a reservation as missing.
 *
 * Binary search over the id-ranged objects only — a report part carries no range
 * and must never be mistaken for a snapshot part.
 */
export const objectForSstId = <
  T extends { idFrom?: string; idTo?: string; kind: string },
>(
  objects: T[],
  sstId: string,
): T | undefined => {
  const ranged = objects
    .filter((o) => o.idFrom !== undefined && o.idTo !== undefined)
    .sort((a, b) => (a.idFrom! < b.idFrom! ? -1 : 1));
  let lo = 0;
  let hi = ranged.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const o = ranged[mid];
    if (sstId < o.idFrom!) hi = mid - 1;
    else if (sstId > o.idTo!) lo = mid + 1;
    else return o;
  }
  return undefined;
};

/**
 * Whether this run stored its FULL snapshots at all.
 *
 * `objectForSstId` answers "which part covers this row"; it cannot distinguish a
 * row outside every range from a run that has no ranges because it was stored
 * with `--no-snapshots` (or whose objects have since aged out on the lifecycle
 * rule). Both come back `undefined`, and only one of them is worth offering the
 * operator a button for. The console asks THIS before it renders a per-row
 * snapshot control, so a run without snapshots says so once, up front, instead of
 * once per row after a click that could never have worked.
 */
export const runStoresSnapshots = (
  objects: { kind: string }[] | undefined,
): boolean => (objects ?? []).some((o) => o.kind === "snapshots");

/**
 * A duration, for a human reading a report or a console row.
 *
 * Shared so the script's closing log line and the console agree: an operator who
 * ran the sweep and then opens the page should not have to reconcile "4m 12s" with
 * "252000ms". Sub-second stays in milliseconds because a 40 ms locker and a 900 ms
 * locker are a meaningful difference at that end, and "0s" hides it.
 */
export const fmtDurationMs = (ms: number | undefined): string => {
  if (ms === undefined || !Number.isFinite(ms) || ms < 0) return "—";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const totalSeconds = Math.round(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const sec = totalSeconds % 60;
  if (h) return `${h}h ${m}m ${sec}s`;
  if (m) return `${m}m ${sec}s`;
  return `${sec}s`;
};

/** The four mutually-exclusive fates of one attempted sync write. */
export const SyncWriteOutcomeList = [
  "applied",
  "failed",
  "refused",
  "skipped",
] as const;

export type SyncWriteOutcome = (typeof SyncWriteOutcomeList)[number];

/**
 * Classify one stored sync-ledger row.
 *
 * Shared between the publisher (which counts these onto the record) and the
 * console (which labels each row with one), because two implementations of the
 * same derivation eventually disagree — and the visible symptom would be a
 * console whose row labels contradict the counts printed above them.
 *
 * The distinction that matters: a `refused` row was stopped by a guard BEFORE any
 * write, so it never had a change set; a `failed` row was a genuine candidate
 * whose write errored. Collapsing them loses the difference between "we decided
 * not to" and "we tried and could not".
 */
export const syncWriteOutcome = (row: {
  applied: boolean;
  error?: string;
  changeCount: number;
}): SyncWriteOutcome => {
  if (row.applied) return "applied";
  if (!row.error) return "skipped";
  return row.changeCount > 0 ? "failed" : "refused";
};
